import RawReactPixel from 'react-facebook-pixel';

import {
	Tracker,
	TrackingEvent,
	TrackerLogger,
	TrackingData,
} from '../tracking.events';

// react-facebook-pixel ships a webpack UMD bundle whose module.exports is
// already `{ __esModule: true, default: {...} }`. Vite's dev pre-bundler
// only unwraps one layer of that, so the default import resolves to the
// outer wrapper (no `.init`) in dev while prod bundling flattens it fine.
// Unwrap defensively so both cases resolve to the actual pixel API.
const ReactPixel: typeof RawReactPixel =
	(RawReactPixel as unknown as { default?: typeof RawReactPixel })
		?.default ?? RawReactPixel;

export class MetaPixelTracker implements Tracker {
	pixelId: string = '';
	constructor(pixelId: string | null) {
		if (!pixelId) return;
		TrackerLogger.info(`Meta Pixel running with tracker id:`, pixelId);
		// const advancedMatching = {};
		this.pixelId = pixelId;
	}

	sendEvent(event: TrackingEvent) {
		const { type } = event;
		switch (type) {
			case 'INSTALLED':
				this.onInstalled();
				break;
			case 'LOGIN':
				this.onLogin();
				break;
			case 'OPEN_TOC':
				this.onOpenToc();
				break;
			case 'CLOSE_TOC':
				this.onCloseToc();
				break;
			case 'SHARE':
				this.onShare(event.data);
				break;
			case 'BTN_CLICK_INSTALL':
				this.onClickInstall();
				break;
			case 'INSTALL_PROMPT_ACCEPTED':
				this.onInstallPromptOutcome('accepted');
				break;
			case 'INSTALL_PROMPT_DISMISSED':
				this.onInstallPromptOutcome('dismissed');
				break;
			case 'BTN_CLICK_LOGIN':
				this.onClickLogIn();
				break;
			case 'BTN_CLICK_LOGOUT':
				this.onClickLogOut();
				break;
			case 'BTN_CLICK_RESPONSIVE':
				this.onClickResponsive(event.data);
				break;
			case 'BTN_CLICK_REPLICA':
				this.onClickReplica(event.data);
				break;
			case 'BTN_CLICK_REPLICA_SINGLE':
				this.onClickReplicaSingle(event.data);
				break;
			case 'BTN_CLICK_REPLICA_DOUBLE':
				this.onClickReplicaDouble(event.data);
				break;
			case 'BTN_CLICK_FAVORITE_ARTICLE':
				this.onClickFavoriteArticle(event.data);
				break;
			case 'BTN_CLICK_UNFAVORITE_ARTICLE':
				this.onClickUnfavoriteArticle(event.data);
				break;
			case 'BTN_CLICK_ISSUE':
				this.onClickIssue(event.data);
				break;
			case 'LANGUAGE_CHANGE':
				this.onChangeLanguage(event.data);
				break;
			default:
				return;
		}
	}

	private onInstalled() {
		ReactPixel.track('Installed', {
			category: 'app',
			nonInteraction: true,
		});
	}

	private onLogin() {
		ReactPixel.track('Login', {
			category: 'session',
		});
	}

	private onOpenToc() {
		ReactPixel.track('OpenTOC', {
			category: 'issue',
		});
	}

	private onCloseToc() {
		ReactPixel.track('CloseTOC', {
			category: 'issue',
		});
	}

	private onShare(data: TrackingData) {
		ReactPixel.track('Share', {
			method: data?.method,
			contentType: data?.contentType,
			itemId: data?.article?.ArticleID,
		});
	}

	private onClickInstall() {
		ReactPixel.track('ClickInstall', {
			category: 'session',
		});
	}

	private onInstallPromptOutcome(outcome: 'accepted' | 'dismissed') {
		ReactPixel.track(
			outcome === 'accepted'
				? 'InstallPromptAccepted'
				: 'InstallPromptDismissed',
			{
				category: 'session',
			},
		);
	}

	private onClickLogIn() {
		ReactPixel.track('ClickLogin', {
			category: 'session',
		});
	}

	private onClickLogOut() {
		ReactPixel.track('ClickLogout', {
			category: 'session',
		});
	}

	private onClickResponsive(data: TrackingData) {
		ReactPixel.track('OnResponsive', data);
	}

	private onClickReplica(data: TrackingData) {
		ReactPixel.track('OnReplica', data);
	}

	private onClickReplicaSingle(data: TrackingData) {
		ReactPixel.track('OnSingleReplica', data);
	}

	private onClickReplicaDouble(data: TrackingData) {
		ReactPixel.track('OnDoubleReplica', data);
	}

	private onClickFavoriteArticle(data: TrackingData) {
		ReactPixel.track('OnFavoriteArticle', data);
	}

	private onClickUnfavoriteArticle(data: TrackingData) {
		ReactPixel.track('OnUnfavoriteArticle', data);
	}

	private onClickIssue(data: TrackingData) {
		ReactPixel.track('OpenIssue', data);
	}

	private onChangeLanguage(data: TrackingData) {
		ReactPixel.track('ChangeLanguage', data);
	}

	static init(pixelId: string) {
		const options = {
			autoConfig: true,
			debug: true,
		};
		ReactPixel.init(pixelId, undefined, options);
	}

	static pageView() {
		ReactPixel.pageView();
	}
}
